import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  buildPlan,
  CAPTURE_MODES,
  creditPosition,
  nextTarget,
  summarise,
  type CaptureModeId,
  type CapturePlan,
} from '@/lib/capture'
import { bearingDegrees, compassPoint, distanceMeters } from '@/lib/geo'
import type { LiveUser } from '@/types/liveview'
import type { ProximiioFeatureCollection } from '@/types/proximi'

const STORAGE_PREFIX = 'liveview:capture:'

/** Progress that has already been earned must survive a page reload. */
function storageKey(modeId: string, level: number) {
  return `${STORAGE_PREFIX}${modeId}:${level}`
}

function loadProgress(modeId: string, level: number): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey(modeId, level))
    return raw ? (JSON.parse(raw) as Record<string, number>) : {}
  } catch {
    return {}
  }
}

function saveProgress(modeId: string, level: number, plan: CapturePlan) {
  try {
    const progress: Record<string, number> = {}
    for (const target of plan.targets) {
      if (target.progress > 0) progress[target.id] = target.progress
    }
    localStorage.setItem(storageKey(modeId, level), JSON.stringify(progress))
  } catch {
    // Losing persistence is survivable; losing the session is not, so never throw.
  }
}

export interface Guidance {
  distanceMeters: number
  bearingDegrees: number
  compass: string
  lng: number
  lat: number
  id: string
}

interface Options {
  modeId: CaptureModeId
  features: ProximiioFeatureCollection | null
  level: number | null
  surveyor: LiveUser | null
}

/**
 * Drives a capture session from the live position stream.
 *
 * The surveyor's fixes are credited against a grid laid over the current floor:
 * stand near a target long enough (or, in scan mode, simply pass through it) and
 * it turns green. Nothing here asks anything new of the phone.
 */
export function useCaptureSession({ modeId, features, level, surveyor }: Options) {
  const [plan, setPlan] = useState<CapturePlan | null>(null)
  const lastCreditRef = useRef<number | null>(null)

  const mode = modeId === 'off' ? null : CAPTURE_MODES[modeId]

  // (Re)build the plan whenever the mode, floor or venue geometry changes.
  useEffect(() => {
    if (!mode || !features || level === null) {
      setPlan(null)
      lastCreditRef.current = null
      return
    }

    const built = buildPlan(features, level, mode)
    if (!built) {
      setPlan(null)
      return
    }

    const stored = loadProgress(mode.id, level)
    for (const target of built.targets) {
      const earned = stored[target.id]
      if (earned) target.progress = earned
    }

    setPlan(built)
    lastCreditRef.current = null
  }, [mode, features, level])

  // Credit the surveyor's latest fix.
  useEffect(() => {
    if (!plan || !mode || !surveyor) return
    // A device on another floor cannot be capturing this one.
    if (typeof surveyor.level === 'number' && level !== null && surveyor.level !== level) return

    const now = Date.now()
    const previous = lastCreditRef.current
    lastCreditRef.current = now
    // First fix of a session establishes the clock; it earns no dwell, and a long
    // gap (tab asleep, operator offline) is clamped rather than credited whole.
    const elapsed = previous === null ? 0 : Math.min((now - previous) / 1000, 5)
    if (elapsed === 0 && mode.requiredDwellSeconds > 0) return

    const updated = creditPosition(plan, mode, surveyor, elapsed)
    if (!updated) return

    const next = { ...plan, targets: updated }
    setPlan(next)
    saveProgress(mode.id, plan.level, next)
    // Keyed on the fix itself: `plan` is intentionally absent, or crediting would
    // re-run on its own output.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyor?.timestamp, surveyor?.lat, surveyor?.lng, mode, level])

  const progress = useMemo(
    () => (plan && mode ? summarise(plan, mode) : null),
    [plan, mode],
  )

  const target = useMemo(
    () => (plan && mode ? nextTarget(plan, mode, surveyor) : null),
    [plan, mode, surveyor],
  )

  const guidance: Guidance | null = useMemo(() => {
    if (!target || !surveyor) return null
    const bearing = bearingDegrees(surveyor, target)
    return {
      id: target.id,
      lng: target.lng,
      lat: target.lat,
      distanceMeters: distanceMeters(surveyor, target),
      bearingDegrees: bearing,
      compass: compassPoint(bearing),
    }
  }, [target, surveyor])

  const reset = useCallback(() => {
    if (!plan || !mode) return
    const cleared = { ...plan, targets: plan.targets.map((t) => ({ ...t, progress: 0 })) }
    setPlan(cleared)
    saveProgress(mode.id, plan.level, cleared)
  }, [plan, mode])

  return { mode, plan, progress, target, guidance, reset }
}
